import { ApiError, type FieldError } from '@vlm/api';
import { isolate, SEPARATOR } from '@vlm/i18n';
import type { Key, T } from '../i18n';

// The API answers in English (the legacy contract). The messages a person can cause are put in
// their language; anything else is shown as the API wrote it.
const EXACT: Record<string, Key> = {
  'Invalid username or password': 'errors.invalidCredentials',
  'Account is deactivated': 'errors.deactivated',
  'Cannot deactivate the last active admin': 'errors.lastAdmin',
  'Current password is incorrect': 'errors.wrongPassword',
  'Vehicle already has an active device assignment': 'errors.vehicleHasDevice',
  'Device is already assigned to another vehicle': 'errors.deviceInUse',
  'Polygon geofence requires at least 3 points': 'errors.polygonCorners',
  'Center coordinates required for RADIUS geofence': 'errors.circleCenter',
  'Radius must be greater than 0': 'errors.circleRadius',
  'Access denied': 'errors.accessDenied',
  // the company's own state (docs/SAAS.md §3): at sign-in, and when a session is cut short
  'Your company is awaiting approval': 'errors.companyPending',
  'Your company account is suspended': 'errors.companySuspended',
  'Your company registration was not approved': 'errors.companyRejected',
  'Your company trial has ended': 'errors.companyTrialEnded',
  'Company account is not active': 'errors.companyInactive',
};

/** Why a session can end before its time: the account or its company was stopped, or its free trial ended without a plan. */
const REFUSALS = new Set([
  'Account is deactivated',
  'Your company is awaiting approval',
  'Your company account is suspended',
  'Your company registration was not approved',
  'Your company trial has ended',
  'Company account is not active',
]);

export const isRefusal = (error: unknown) => error instanceof ApiError && REFUSALS.has(error.message);

/** The plan's limits (PlanLimits on the API): "Your plan allows up to 5 active vehicles". */
const LIMITS: Array<[RegExp, Key]> = [
  [/^Your plan allows up to (\d+) active vehicles$/, 'errors.planVehicles'],
  [/^Your plan allows up to (\d+) active users$/, 'errors.planUsers'],
];

const DUPLICATES: Array<[RegExp, Key]> = [
  [/^Vehicle already exists with plateNumber: (.+)$/, 'errors.duplicatePlate'],
  [/^Device already exists with IMEI: (.+)$/, 'errors.duplicateImei'],
  [/^User already exists with username: (.+)$/, 'errors.duplicateUsername'],
];

/** The API's field names, labelled as the forms label them. */
const FIELD: Record<string, Key> = {
  username: 'users.username',
  fullName: 'users.fullName',
  email: 'users.email',
  phone: 'users.phone',
  password: 'users.password',
  language: 'users.language',
  currentPassword: 'profile.currentPassword',
  newPassword: 'profile.newPassword',
  vehicleName: 'vehicles.name',
  plateNumber: 'vehicles.plate',
  vehicleType: 'vehicles.type',
  model: 'vehicles.model',
  year: 'vehicles.year',
  color: 'vehicles.color',
  notes: 'common.notes',
  deviceName: 'devices.name',
  imei: 'devices.imei',
  simNumber: 'devices.sim',
  networkProvider: 'devices.provider',
  name: 'geofences.name',
  radiusMeters: 'geofences.radius',
  centerLatitude: 'geofences.center',
  centerLongitude: 'geofences.center',
  polygonPoints: 'geofences.corners',
  emailRecipients: 'alertSettings.emailRecipients',
  cooldownMinutes: 'alertSettings.cooldown',
};

const PROBLEMS = new Set(['NotNull', 'NotBlank', 'NotEmpty', 'Email', 'Pattern', 'Min', 'Max', 'Positive']);

/** One field the API turned down (ErrorResponse.errors), in the reader's language. */
export function fieldErrorText(e: FieldError, t: T): string {
  const name = e.field.replace(/^\[\d+\]\./, '').replace(/\[\d+\].*$/, '');
  const field = FIELD[name] ? t(FIELD[name]) : isolate(name);
  const p = e.params ?? {};
  let problem: string;
  if (e.code === 'Size' && name === 'emailRecipients') {
    problem = t('alertSettings.maxAddresses', { max: p.max ?? '' }); // a count of addresses, not of characters
  } else if (e.code === 'Size') {
    if (p.max === undefined) problem = t('validation.SizeMin', { min: p.min ?? 0 });
    else if (!p.min) problem = t('validation.SizeMax', { max: p.max });
    else problem = t('validation.Size', { min: p.min, max: p.max });
  } else if (e.code === 'DecimalMin' || e.code === 'DecimalMax') {
    problem = t(e.code === 'DecimalMin' ? 'validation.Min' : 'validation.Max', { value: p.value ?? '' });
  } else if (PROBLEMS.has(e.code)) {
    problem = t(`validation.${e.code as 'NotNull'}`, { value: p.value ?? '' });
  } else {
    problem = t('validation.invalid');
  }
  return t('validation.field', { field, problem });
}

/** What to tell the person when `error` stopped them. */
export function errorText(error: unknown, t: T): string {
  if (error instanceof ApiError) {
    const exact = EXACT[error.message];
    if (exact) return t(exact);
    for (const [pattern, key] of DUPLICATES) {
      const m = pattern.exec(error.message);
      if (m) return t(key, { value: isolate(m[1]) });
    }
    for (const [pattern, key] of LIMITS) {
      const m = pattern.exec(error.message);
      if (m) return t(key, { count: Number(m[1]) });
    }
    const fields = error.body?.errors;
    if (fields?.length) return fields.map((e) => fieldErrorText(e, t)).join(SEPARATOR);
    if (error.status === 404) return t('errors.notFound');
    if (error.status === 403) return t('errors.accessDenied');
    if (error.status >= 500) return t('errors.server', { status: error.status });
    return error.message || t('errors.unknown');
  }
  // fetch() rejects with a TypeError when the network or CORS fails
  if (error instanceof TypeError) return t('errors.network');
  return t('errors.unknown');
}

export const isNotFound = (error: unknown) => error instanceof ApiError && error.status === 404;
